import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  useEnhancedCreditPackages,
  type EnhancedCreditPackage,
} from "@/hooks/use-enhanced-credit-packages";

export interface EnrichedCreditPackage extends EnhancedCreditPackage {
  device_name?: string | null;
  device_serial?: string | null;
}

interface DeviceLookupEntry {
  device_name: string | null;
  device_serial: string | null;
  images_allowed: number | null;
  images_current: number | null;
}

export type DeviceDetailsLookup = Record<string, DeviceLookupEntry>;

/**
 * Wraps useEnhancedCreditPackages and attaches friendly device_name + device_serial
 * from device_details for every package, without changing the underlying
 * package-centric data model.
 */
export const useCreditPackagesWithDevices = () => {
  const packagesQuery = useEnhancedCreditPackages();
  const packages = packagesQuery.data ?? [];

  const deviceIds = Array.from(
    new Set(
      packages
        .map((p) => p.device_id)
        .filter((id): id is string => !!id && id !== "USER_OWNED")
    )
  );

  const devicesQuery = useQuery({
    queryKey: ["device-details-lookup", deviceIds.sort().join(",")],
    enabled: deviceIds.length > 0,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("device_details")
        .select("device_id, device_name, device_serial, images_allowed, images_current")
        .in("device_id", deviceIds);

      if (error) throw error;

      const map: DeviceDetailsLookup = {};
      for (const row of data ?? []) {
        map[row.device_id] = {
          device_name: row.device_name ?? null,
          device_serial: row.device_serial ?? null,
          images_allowed: row.images_allowed ?? null,
          images_current: row.images_current ?? null,
        };
      }
      return map;
    },
  });

  const lookup: DeviceDetailsLookup = devicesQuery.data ?? {};

  const enriched: EnrichedCreditPackage[] = packages.map((pkg) => {
    const info = lookup[pkg.device_id];
    return {
      ...pkg,
      device_name: info?.device_name ?? null,
      device_serial: info?.device_serial ?? null,
    };
  });

  return {
    ...packagesQuery,
    data: enriched,
    deviceLookup: lookup,
    isLoading: packagesQuery.isLoading || devicesQuery.isLoading,
  };
};

export const formatDeviceLabel = (pkg: {
  device_id: string;
  device_name?: string | null;
  device_serial?: string | null;
}) => {
  if (pkg.device_id === "USER_OWNED") return "User Owned (unallocated)";
  return pkg.device_name || pkg.device_serial || pkg.device_id;
};

export const formatDeviceSubLabel = (pkg: {
  device_id: string;
  device_serial?: string | null;
}) => {
  const parts: string[] = [];
  if (pkg.device_serial) parts.push(`SN ${pkg.device_serial}`);
  if (pkg.device_id && pkg.device_id !== "USER_OWNED") {
    parts.push(`ID ${pkg.device_id.slice(0, 8)}…`);
  }
  return parts.join(" · ");
};
